import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  providerApi,
  type ProviderFoodOrder,
  type ProviderFoodOrderStatus,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { subscribeRealtimeConnection, subscribeRealtimeEvent } from "../../lib/realtime";
import { isOpenOrder } from "./order-status";

const POLL_INTERVAL_MS = 20000;
/** Finished orders stay on the board this long so staff can see what just went out. */
const RECENT_HOURS = 6;

/**
 * The venue's live food and drink queue: every open order, plus orders served
 * or cancelled in the last few hours. Refreshes on realtime order events and
 * polls as a fallback.
 */
export function useFoodOrderQueue(venueId: string | null | undefined, options?: { includeRecent?: boolean }) {
  const { t } = useAppPreferences();
  const includeRecent = options?.includeRecent ?? true;
  const [orders, setOrders] = useState<ProviderFoodOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  const requestRef = useRef(0);
  const venueRef = useRef(venueId);
  venueRef.current = venueId;

  const reload = useCallback(async () => {
    if (!venueId) {
      setOrders([]);
      setError(null);
      return;
    }
    const requestId = ++requestRef.current;
    setIsLoading(true);
    try {
      const response = await providerApi.listFoodOrders(
        includeRecent
          ? { venueId, statuses: ["delivered", "cancelled"], sinceHours: RECENT_HOURS }
          : { venueId, statuses: ["pending", "preparing", "ready"] },
        { networkOnly: true },
      );
      if (requestId !== requestRef.current || venueRef.current !== venueId) return;
      setOrders(response.orders);
      setError(null);
    } catch (requestError) {
      if (requestId !== requestRef.current) return;
      setError(requestError instanceof ApiError && requestError.status !== 0
        ? requestError.detail
        : t("Could not load orders. Check your connection."));
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, [includeRecent, t, venueId]);

  useEffect(() => {
    setOrders([]);
    void reload();
  }, [reload]);

  useEffect(() => subscribeRealtimeEvent((event) => {
    if (event.type === "food_order.created" || event.type === "food_order.updated") {
      if (!venueRef.current || event.payload?.venue_id === venueRef.current) void reload();
    } else if (event.type === "resync" || event.type === "session.stopped") {
      void reload();
    }
  }), [reload]);

  useEffect(() => subscribeRealtimeConnection((connected) => {
    if (connected) void reload();
  }), [reload]);

  useEffect(() => {
    if (!venueId) return;
    const intervalId = setInterval(() => void reload(), POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [reload, venueId]);

  const upsert = useCallback((order: ProviderFoodOrder) => {
    setOrders((current) => {
      const index = current.findIndex((existing) => existing.id === order.id);
      if (index === -1) return [order, ...current];
      const next = current.slice();
      next[index] = order;
      return next;
    });
  }, []);

  const moveOrder = useCallback(async (
    orderId: string,
    status: ProviderFoodOrderStatus,
    cancelReason?: string | null,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    setUpdatingOrderId(orderId);
    try {
      const updated = await providerApi.updateFoodOrderStatus(orderId, status, cancelReason);
      upsert(updated);
      return { ok: true };
    } catch (requestError) {
      const message = requestError instanceof ApiError && requestError.status !== 0
        ? requestError.detail
        : t("Could not update the order. Check your connection and try again.");
      // The order may have moved on another screen; show the latest state.
      void reload();
      return { ok: false, message };
    } finally {
      setUpdatingOrderId(null);
    }
  }, [reload, t, upsert]);

  const openCount = orders.filter(isOpenOrder).length;
  const newCount = orders.filter((order) => order.status === "pending").length;

  return { orders, openCount, newCount, isLoading, error, reload, moveOrder, upsert, updatingOrderId };
}
